import pointOfSale from "@faire/web-api/routes/point-of-sale";
import { Typography } from "@faire/web/slate/Typography";
import { PageLoader } from "@faire/web/ui/CircleSpinner";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { Suspense } from "react";
import { Redirect } from "react-router-dom";

import { LazyBulkEditorEducationModal } from "@faire/retailer/features/PointOfSale/components/NotificationModals/LazyBulkEditorEducationModal";
import { LazyNewSettingNotifyModal } from "@faire/retailer/features/PointOfSale/components/NotificationModals/LazyNewSettingNotifyModal";
import { usePOSSummary } from "@faire/retailer/features/PointOfSale/hooks/usePOSSummary";
import { useMilestone } from "@faire/retailer/stores/domain/useMilestone";
import { useAppModal } from "@faire/retailer/stores/ui/AppModalStore";
import { usePromoModalToDisplay } from "@faire/retailer/stores/ui/PromoModalStore";

interface IProps extends React.PropsWithChildren {
  linkedOnly?: true;
  allowNotifications?: true;
}

export const SummaryWall: React.FC<IProps> = ({
  linkedOnly = false,
  allowNotifications = false,
  children,
}) => {
  const { isLoading, isError, hasLinkedPOS } = usePOSSummary();
  const { isModalShowing } = useAppModal();
  const { modalToDisplay } = usePromoModalToDisplay();
  const shouldShowNotificationModal =
    allowNotifications && hasLinkedPOS && !isModalShowing && !modalToDisplay;

  if (isLoading) {
    return <PageLoader />;
  }

  if (isError) {
    return (
      <Typography>
        <StrictLocalMsg defaultMessage="Something went wrong, please try again." />
      </Typography>
    );
  }

  return linkedOnly && !hasLinkedPOS ? (
    <Redirect to={pointOfSale()} />
  ) : (
    <>
      {children}
      {shouldShowNotificationModal ? <NotificationModal /> : null}
    </>
  );
};

const NotificationModal: React.FC = () => {
  const { hasOccurred: variantsModalSeen } = useMilestone(
    "HAS_SEEN_POS_NEW_FEATURE_MODAL"
  );
  const { hasOccurred: bulkEditorModalSeen } = useMilestone(
    "POS_BULK_EDITOR_EDUCATION_MODAL_SEEN"
  );

  if (!bulkEditorModalSeen) {
    return (
      <Suspense>
        <LazyBulkEditorEducationModal />
      </Suspense>
    );
  }

  if (!variantsModalSeen) {
    // This component and the associated milestone have been poorly named
    // Won't change it as they should soon be removed
    return (
      <Suspense>
        <LazyNewSettingNotifyModal />
      </Suspense>
    );
  }

  return null;
};
